import React, { useState, useEffect } from 'react';
import { locationAPI, engineerAPI } from '../services/api';

const LiveMap = () => {
  const [engineers, setEngineers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadLocations = async () => {
    try {
      const res = await locationAPI.getLive();
      setEngineers(res.data.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (engineerId) => {
    try {
      const res = await locationAPI
        .getHistory(engineerId);
      setHistory(res.data.data.locations || []);
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  const statusColor = (status) => {
    if (status === 'active') return 'var(--green)';
    if (status === 'idle') return 'var(--yellow)';
    return 'var(--text3)';
  };

  const activeCount = engineers.filter(
    e => e.current_status === 'active').length;
  const idleCount = engineers.filter(
    e => e.current_status === 'idle').length;
  const offlineCount = engineers.filter(
    e => e.current_status === 'offline').length;

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Live Map
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Real-time engineer tracking
            {lastUpdated && (
              <span style={{
                fontFamily:'DM Mono,monospace',
                marginLeft:'10px',
              }}>
                · Last updated:{' '}
                {lastUpdated.toLocaleTimeString(
                  'en-IN',
                  { hour:'2-digit',
                    minute:'2-digit' }
                )}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={loadLocations}
          style={{
            background:'var(--surface2)',
            color:'var(--text2)',
            border:'1px solid var(--border2)',
            padding:'8px 16px',
            borderRadius:'8px',
            fontSize:'13px', cursor:'pointer',
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Status Summary */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(3,1fr)',
        gap:'14px', marginBottom:'20px',
      }}>
        {[
          { label:'On Field', value:activeCount,
            color:'var(--green)' },
          { label:'Idle', value:idleCount,
            color:'var(--yellow)' },
          { label:'Offline', value:offlineCount,
            color:'var(--text3)' },
        ].map(s => (
          <div key={s.label} style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'10px', padding:'16px',
            borderTop:`2px solid ${s.color}`,
            display:'flex',
            alignItems:'center', gap:'16px',
          }}>
            <div style={{
              fontFamily:'Syne,sans-serif',
              fontSize:'32px', fontWeight:'800',
              color: s.color,
            }}>
              {s.value}
            </div>
            <div style={{
              fontSize:'13px',
              color:'var(--text2)',
            }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Map + Engineers */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'1fr 320px',
        gap:'16px',
      }}>
        {/* Map Area */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          <div style={{
            padding:'16px 20px',
            borderBottom:'1px solid var(--border)',
            fontFamily:'Syne,sans-serif',
            fontSize:'14px', fontWeight:'700',
            display:'flex',
            justifyContent:'space-between',
            alignItems:'center',
          }}>
            Engineer Locations
            <span style={{
              fontSize:'11px',
              fontFamily:'DM Mono,monospace',
              color:'var(--text3)',
            }}>
              Auto-refreshes every 2 hours
            </span>
          </div>

          {/* Map Visualization */}
          <div style={{
            height:'400px',
            background:`
              radial-gradient(ellipse at 30% 40%,
                rgba(0,212,255,0.06) 0%,
                transparent 50%),
              radial-gradient(ellipse at 70% 70%,
                rgba(159,122,234,0.05) 0%,
                transparent 40%),
              repeating-linear-gradient(0deg,
                transparent, transparent 39px,
                rgba(255,255,255,0.03) 39px,
                rgba(255,255,255,0.03) 40px),
              repeating-linear-gradient(90deg,
                transparent, transparent 39px,
                rgba(255,255,255,0.03) 39px,
                rgba(255,255,255,0.03) 40px)
            `,
            position:'relative',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
          }}>
            {loading ? (
              <div style={{
                color:'var(--text3)',
                fontFamily:'DM Mono,monospace',
                fontSize:'13px',
              }}>
                Loading engineer locations...
              </div>
            ) : engineers.length === 0 ? (
              <div style={{
                textAlign:'center',
                color:'var(--text3)',
              }}>
                <div style={{
                  fontSize:'40px',
                  marginBottom:'12px',
                }}>
                  ◎
                </div>
                <div style={{
                  fontSize:'14px',
                  color:'var(--text2)',
                }}>
                  No engineers online
                </div>
                <div style={{
                  fontSize:'12px',
                  marginTop:'6px',
                }}>
                  Engineers appear here when
                  they log in to the mobile app
                </div>
              </div>
            ) : (
              <>
                {/* Engineer Dots */}
                {engineers.map((eng, i) => {
                  const positions = [
                    { left:'25%', top:'35%' },
                    { left:'55%', top:'25%' },
                    { left:'70%', top:'55%' },
                    { left:'40%', top:'65%' },
                    { left:'80%', top:'30%' },
                    { left:'15%', top:'60%' },
                  ];
                  const pos = positions[
                    i % positions.length];
                  return (
                    <div key={eng.id}
                      onClick={() => {
                        setSelected(eng);
                        loadHistory(eng.id);
                      }}
                      style={{
                        position:'absolute',
                        left: eng.current_longitude
                          ? `${((parseFloat(eng.current_longitude) - 68) / (97 - 68)) * 100}%`
                          : pos.left,
                        top: eng.current_latitude
                          ? `${100 - ((parseFloat(eng.current_latitude) - 8) / (37 - 8)) * 100}%`
                          : pos.top,
                        width:'14px', height:'14px',
                        borderRadius:'50%',
                        background: statusColor(
                          eng.current_status),
                        boxShadow: `0 0 0 3px ${statusColor(eng.current_status)}44, 0 0 12px ${statusColor(eng.current_status)}66`,
                        cursor:'pointer',
                        zIndex:2,
                        transition:'all 0.3s',
                        transform:
                          selected?.id === eng.id
                            ? 'scale(1.5)' : 'scale(1)',
                      }}
                      title={eng.name}
                    />
                  );
                })}

                {/* Legend */}
                <div style={{
                  position:'absolute',
                  top:'12px', right:'12px',
                  background:
                    'rgba(17,22,32,0.9)',
                  border:
                    '1px solid var(--border)',
                  borderRadius:'8px',
                  padding:'10px 14px',
                  display:'flex',
                  flexDirection:'column',
                  gap:'6px',
                }}>
                  {[
                    { color:'var(--green)',
                      label:'On Field' },
                    { color:'var(--yellow)',
                      label:'Idle' },
                    { color:'var(--text3)',
                      label:'Offline' },
                  ].map(l => (
                    <div key={l.label} style={{
                      display:'flex',
                      alignItems:'center',
                      gap:'7px', fontSize:'11px',
                      color:'var(--text2)',
                      fontFamily:
                        'DM Mono,monospace',
                    }}>
                      <div style={{
                        width:'8px', height:'8px',
                        borderRadius:'50%',
                        background: l.color,
                      }} />
                      {l.label}
                    </div>
                  ))}
                </div>

                <div style={{
                  position:'absolute',
                  bottom:'12px', left:'16px',
                  fontSize:'10px',
                  fontFamily:'DM Mono,monospace',
                  color:'var(--text3)',
                }}>
                  Click on a dot to view
                  engineer details
                </div>
              </>
            )}
          </div>

          {/* Location Note */}
          <div style={{
            padding:'12px 20px',
            background:'rgba(0,212,255,0.05)',
            borderTop:'1px solid var(--border)',
            fontSize:'12px',
            color:'var(--text3)',
          }}>
            📍 GPS coordinates are captured
            from the IRONWALL Field mobile app.
            Location updates every 2 hours
            automatically.
          </div>
        </div>

        {/* Engineer List */}
        <div style={{
          display:'flex',
          flexDirection:'column', gap:'10px',
        }}>
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
          }}>
            <div style={{
              padding:'14px 16px',
              borderBottom:
                '1px solid var(--border)',
              fontFamily:'Syne,sans-serif',
              fontSize:'13px', fontWeight:'700',
            }}>
              All Engineers
            </div>
            <div>
              {loading ? (
                <div style={{
                  padding:'20px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'12px',
                }}>
                  Loading...
                </div>
              ) : engineers.length === 0 ? (
                <div style={{
                  padding:'20px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'12px',
                }}>
                  No engineers found
                </div>
              ) : (
                engineers.map(eng => (
                  <div key={eng.id}
                    onClick={() => {
                      setSelected(
                        selected?.id === eng.id
                          ? null : eng
                      );
                      if (selected?.id !== eng.id)
                        loadHistory(eng.id);
                    }}
                    style={{
                      padding:'12px 16px',
                      borderBottom:
                        '1px solid var(--border)',
                      cursor:'pointer',
                      background:
                        selected?.id === eng.id
                          ? 'rgba(0,212,255,0.05)'
                          : 'transparent',
                      transition:'all 0.15s',
                    }}
                  >
                    <div style={{
                      display:'flex',
                      justifyContent:
                        'space-between',
                      alignItems:'center',
                      marginBottom:'4px',
                    }}>
                      <div style={{
                        fontWeight:'600',
                        fontSize:'13px',
                      }}>
                        {eng.name}
                      </div>
                      <div style={{
                        display:'flex',
                        alignItems:'center',
                        gap:'5px',
                        fontSize:'10px',
                        fontFamily:
                          'DM Mono,monospace',
                        color: statusColor(
                          eng.current_status),
                      }}>
                        <div style={{
                          width:'6px',
                          height:'6px',
                          borderRadius:'50%',
                          background: statusColor(
                            eng.current_status),
                        }} />
                        {eng.current_status}
                      </div>
                    </div>
                    <div style={{
                      fontSize:'11px',
                      color:'var(--text3)',
                      fontFamily:
                        'DM Mono,monospace',
                    }}>
                      {eng.current_ticket
                        ? `On: ${eng.current_ticket}`
                        : 'No active ticket'}
                    </div>
                    {eng.last_ping_at && (
                      <div style={{
                        fontSize:'10px',
                        color:'var(--text3)',
                        marginTop:'3px',
                      }}>
                        Last ping:{' '}
                        {new Date(eng.last_ping_at)
                          .toLocaleTimeString(
                            'en-IN',
                            { hour:'2-digit',
                              minute:'2-digit' }
                          )}
                      </div>
                    )}
                    {eng.current_latitude && (
                      <div style={{
                        fontSize:'10px',
                        color:'var(--accent)',
                        fontFamily:
                          'DM Mono,monospace',
                        marginTop:'3px',
                      }}>
                        📍{' '}
                        {parseFloat(
                          eng.current_latitude
                        ).toFixed(4)}°N,{' '}
                        {parseFloat(
                          eng.current_longitude
                        ).toFixed(4)}°E
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Location History */}
          {selected && history.length > 0 && (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              overflow:'hidden',
            }}>
              <div style={{
                padding:'14px 16px',
                borderBottom:
                  '1px solid var(--border)',
                fontFamily:'Syne,sans-serif',
                fontSize:'13px', fontWeight:'700',
              }}>
                Today's Pings — {selected.name}
              </div>
              <div style={{
                padding:'12px 16px',
              }}>
                {history.map((h, i) => (
                  <div key={i} style={{
                    display:'flex',
                    gap:'10px',
                    paddingBottom:'10px',
                    position:'relative',
                  }}>
                    {i < history.length - 1 && (
                      <div style={{
                        position:'absolute',
                        left:'5px', top:'14px',
                        bottom:0, width:'1px',
                        background:'var(--border)',
                      }} />
                    )}
                    <div style={{
                      width:'10px', height:'10px',
                      borderRadius:'50%',
                      background:'var(--accent)',
                      flexShrink:0, marginTop:'3px',
                      zIndex:1,
                    }} />
                    <div>
                      <div style={{
                        fontSize:'11px',
                        fontFamily:
                          'DM Mono,monospace',
                        color:'var(--accent)',
                      }}>
                        {parseFloat(h.latitude)
                          .toFixed(4)}°N,{' '}
                        {parseFloat(h.longitude)
                          .toFixed(4)}°E
                      </div>
                      <div style={{
                        fontSize:'10px',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        {new Date(h.created_at)
                          .toLocaleTimeString(
                            'en-IN',
                            { hour:'2-digit',
                              minute:'2-digit' }
                          )}
                        {' · '}{h.ping_type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMap;